import { LngLatArray } from "../flights/types";

export function cubicSpline(
    input: LngLatArray[],
    numberOfSegments: number,
    minSegmentLength = Number.POSITIVE_INFINITY
): LngLatArray[][] {
    if (input.length < 2) {
        return [];
    }
    if (input.length === 2) {
        return [[input[0], input[1]]];
    }

    const unwrapped = unwrapLongitudes(input);
    const n = unwrapped.length - 1;

    // Chord-length interval widths used as the spline parameter
    const h = new Float64Array(n);
    for (let i = 0; i < n; i++) {
        const dx = unwrapped[i + 1][0] - unwrapped[i][0];
        const dy = unwrapped[i + 1][1] - unwrapped[i][1];
        h[i] = Math.sqrt(dx * dx + dy * dy) || 1e-10;
    }

    const xs = unwrapped.map((p) => p[0]);
    const ys = unwrapped.map((p) => p[1]);
    const Mx = splineMoments(xs, h, n);
    const My = splineMoments(ys, h, n);

    const result: LngLatArray[][] = [];
    for (let i = 0; i < n; i++) {
        if (isLongSegment(unwrapped[i], unwrapped[i + 1], minSegmentLength)) {
            result.push([unwrapped[i], unwrapped[i + 1]]);
            continue;
        }

        const segment: LngLatArray[] = [unwrapped[i]];
        for (let step = 1; step < numberOfSegments; step++) {
            const localT = (step / numberOfSegments) * h[i];
            segment.push([evalCubic(xs, Mx, h, i, localT), evalCubic(ys, My, h, i, localT)]);
        }
        segment.push(unwrapped[i + 1]);
        result.push(segment);
    }
    return result;
}

// Solves for second derivatives M[0..n] via the natural cubic spline tridiagonal system (M[0]=M[n]=0).
function splineMoments(coords: number[], h: Float64Array, n: number): Float64Array {
    const M = new Float64Array(n + 1);
    const size = n - 1;
    if (size <= 0) {
        return M;
    }

    const diag = new Float64Array(size);
    const upper = new Float64Array(size);
    const rhs = new Float64Array(size);
    for (let k = 0; k < size; k++) {
        diag[k] = 2 * (h[k] + h[k + 1]);
        upper[k] = h[k + 1];
        rhs[k] = 6 * ((coords[k + 2] - coords[k + 1]) / h[k + 1] - (coords[k + 1] - coords[k]) / h[k]);
    }

    // Thomas algorithm (forward sweep)
    const cp = new Float64Array(size);
    const dp = new Float64Array(size);
    cp[0] = upper[0] / diag[0];
    dp[0] = rhs[0] / diag[0];
    for (let k = 1; k < size; k++) {
        const denom = diag[k] - h[k] * cp[k - 1];
        cp[k] = upper[k] / denom;
        dp[k] = (rhs[k] - h[k] * dp[k - 1]) / denom;
    }

    // Back substitution
    M[n - 1] = dp[size - 1];
    for (let k = size - 2; k >= 0; k--) {
        M[k + 1] = dp[k] - cp[k] * M[k + 2];
    }
    return M;
}

function evalCubic(coords: number[], M: Float64Array, h: Float64Array, i: number, t: number): number {
    const hi = h[i];
    const b = (coords[i + 1] - coords[i]) / hi - (hi * (2 * M[i] + M[i + 1])) / 6;
    const c = M[i] / 2;
    const d = (M[i + 1] - M[i]) / (6 * hi);
    return coords[i] + t * (b + t * (c + t * d));
}

export function CatmullRom(
    input: LngLatArray[],
    numberOfSegments: number,
    alpha: number,
    minSegmentLength = Number.POSITIVE_INFINITY
): LngLatArray[][] {
    // should return smoothed segments
    // output[0] should contain the list of points starting with input[0] and ending with input[1]
    // output[1] should contain the list of points starting with input[1] and ending with input[2]
    if (input.length < 2) {
        return [];
    }

    if (input.length === 2) {
        return [[input[0], input[1]]];
    }

    const unwrapped = unwrapLongitudes(input);
    const result: LngLatArray[][] = [];

    for (let i = 0; i < unwrapped.length - 1; i++) {
        const p1 = unwrapped[i];
        const p2 = unwrapped[i + 1];

        if (isLongSegment(p1, p2, minSegmentLength)) {
            result.push([p1, p2]);
            continue;
        }

        if (Math.abs(p1[0] - p2[0]) < 0.001 && Math.abs(p1[1] - p2[1]) < 0.001) {
            result.push([p1, p2]);
            continue;
        }

        const p0 = i > 0 ? unwrapped[i - 1] : extrapolateBack(p1, p2);
        const p3 = i + 2 < unwrapped.length ? unwrapped[i + 2] : extrapolateForward(p1, p2);

        const t0 = 0;
        const t1 = t0 + chord(p0, p1, alpha);
        const t2 = t1 + chord(p1, p2, alpha);
        const t3 = t2 + chord(p2, p3, alpha);

        const segment: LngLatArray[] = [];
        for (let step = 0; step <= numberOfSegments; step++) {
            if (step === 0) {
                segment.push(unwrapped[i]);
                continue;
            }
            if (step === numberOfSegments) {
                segment.push(unwrapped[i + 1]);
                continue;
            }

            const t = t1 + (step / numberOfSegments) * (t2 - t1);
            const point = catmullRomPoint(p0, p1, p2, p3, t0, t1, t2, t3, t);
            segment.push([point[0], point[1]]);
        }

        result.push(segment);
    }

    return result;
}

function isLongSegment(p1: LngLatArray, p2: LngLatArray, minSegmentLength: number): boolean {
    return Math.abs(p1[0] - p2[0]) > minSegmentLength || Math.abs(p1[1] - p2[1]) > minSegmentLength;
}

function catmullRomPoint(
    p0: LngLatArray,
    p1: LngLatArray,
    p2: LngLatArray,
    p3: LngLatArray,
    t0: number,
    t1: number,
    t2: number,
    t3: number,
    t: number
): LngLatArray {
    const a1 = interpolate(p0, p1, t0, t1, t);
    const a2 = interpolate(p1, p2, t1, t2, t);
    const a3 = interpolate(p2, p3, t2, t3, t);

    const b1 = interpolate(a1, a2, t0, t2, t);
    const b2 = interpolate(a2, a3, t1, t3, t);

    return interpolate(b1, b2, t1, t2, t);
}

function interpolate(p0: LngLatArray, p1: LngLatArray, t0: number, t1: number, t: number): LngLatArray {
    const dt = t1 - t0;
    if (dt === 0) {
        return p0;
    }

    const w0 = (t1 - t) / dt;
    const w1 = (t - t0) / dt;
    return [w0 * p0[0] + w1 * p1[0], w0 * p0[1] + w1 * p1[1]];
}

function chord(p0: LngLatArray, p1: LngLatArray, alpha: number): number {
    const dx = p1[0] - p0[0];
    const dy = p1[1] - p0[1];
    const sqrDist = dx * dx + dy * dy;
    return Math.pow(sqrDist, alpha * 0.5);
}

function extrapolateBack(p1: LngLatArray, p2: LngLatArray): LngLatArray {
    return [2 * p1[0] - p2[0], 2 * p1[1] - p2[1]];
}

function extrapolateForward(p1: LngLatArray, p2: LngLatArray): LngLatArray {
    return [2 * p2[0] - p1[0], 2 * p2[1] - p1[1]];
}

function unwrapLongitudes(points: LngLatArray[]): LngLatArray[] {
    if (points.length === 0) {
        return [];
    }

    const unwrapped: LngLatArray[] = [[points[0][0], points[0][1]]];
    for (let i = 1; i < points.length; i++) {
        const rawDelta = points[i][0] - points[i - 1][0];
        // Normalize delta to [-180, 180] to detect the shortest crossing direction
        const delta = rawDelta - 360 * Math.round(rawDelta / 360);
        unwrapped.push([unwrapped[i - 1][0] + delta, points[i][1]]);
    }

    return unwrapped;
}
